import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, ArrowRight } from 'lucide-react';
import { getMenuInfoForPath, findAllowedMenuLabel } from '@/config/menuInfo';
import { useAuth } from '@/contexts/AuthContext';

/**
 * Route-aware onboarding tour.
 *
 * Reads the current pathname, resolves the owning "menu" (e.g. /pcf/*, /lca/*,
 * /compliance/*), looks up that menu's step list in the shell menuInfo
 * registry, and shows a small floating card the first time the user visits
 * that menu. State is per-menu in localStorage — dismissing the PCF tour does
 * not silence the tours for LCA, Reporting, etc.
 *
 * Menus without a `tourSteps` array (or with an empty one) render nothing.
 */
export function MenuTour(): JSX.Element | null {
  const { pathname } = useLocation();
  const { getAllowedMenus } = useAuth();
  const info = useMemo(() => {
    const base = getMenuInfoForPath(pathname);
    if (!base) return base;
    // Title = the menu name configured in super-admin (falls back to the static one).
    const dbLabel = findAllowedMenuLabel(getAllowedMenus() as Array<{ label?: string; path?: string; children?: unknown[] }>, pathname);
    return dbLabel ? { ...base, title: dbLabel } : base;
  }, [pathname, getAllowedMenus]);
  const steps = info?.tourSteps ?? [];
  const storageKey = info ? `esg.tour.completed.${info.key}` : '';

  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  // Reset when the resolved menu changes.
  useEffect(() => {
    setStep(0);
    if (!info || steps.length === 0) {
      setVisible(false);
      return;
    }
    try {
      const done = localStorage.getItem(storageKey);
      setVisible(!done);
    } catch {
      setVisible(false);
    }
  }, [info?.key, steps.length, storageKey, info]);

  if (!info || !visible || steps.length === 0) return null;

  const close = (): void => {
    setVisible(false);
    try {
      localStorage.setItem(storageKey, '1');
    } catch {
      /* silent */
    }
  };

  const current = steps[step];
  const isLast = step === steps.length - 1;

  return (
    <div className="fixed bottom-4 left-4 z-[900] w-[320px] rounded-lg border border-slate-200 bg-white shadow-2xl dark:bg-slate-900 dark:border-slate-700">
      <header className="flex items-start justify-between px-4 pt-3 pb-1">
        <div className="text-[10px] uppercase tracking-wide text-blue-700 font-semibold">
          {info.title} · step {step + 1} / {steps.length}
        </div>
        <button
          type="button"
          onClick={close}
          className="text-slate-400 hover:text-slate-700"
          aria-label="Skip tour"
          title="Skip tour"
        >
          <X className="w-3.5 h-3.5" aria-hidden="true" />
        </button>
      </header>
      <div className="px-4 pb-3">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
          {current.title}
        </h3>
        <p className="text-xs text-slate-600 dark:text-slate-300 mt-1">{current.body}</p>
      </div>
      <footer className="flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-slate-50 rounded-b-lg dark:bg-slate-800 dark:border-slate-700">
        <button
          type="button"
          onClick={close}
          className="text-[11px] text-slate-500 hover:text-slate-700 font-medium"
        >
          Skip
        </button>
        <div className="flex items-center gap-2">
          {current.cta && (
            <a
              href={current.cta.href}
              onClick={close}
              className="text-[11px] px-2 py-1 rounded-md bg-slate-100 text-slate-700 hover:bg-slate-200 font-medium dark:bg-slate-700 dark:text-slate-200"
            >
              {current.cta.label}
            </a>
          )}
          <button
            type="button"
            onClick={() => (isLast ? close() : setStep(step + 1))}
            className="text-[11px] px-3 py-1 rounded-md bg-blue-600 text-white font-medium hover:bg-blue-700 inline-flex items-center gap-1"
          >
            {isLast ? 'Done' : 'Next'}
            {!isLast && <ArrowRight className="w-3 h-3" aria-hidden="true" />}
          </button>
        </div>
      </footer>
    </div>
  );
}

/**
 * Clear the completion flag for the current menu so the tour replays on next
 * visit. Wire this into a footer link or a "?" button in Header for users who
 * want to re-take the tour.
 */
export function resetMenuTour(pathname: string): void {
  const info = getMenuInfoForPath(pathname);
  if (!info) return;
  try {
    localStorage.removeItem(`esg.tour.completed.${info.key}`);
  } catch {
    /* silent */
  }
  window.location.reload();
}

export default MenuTour;
